<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import {
  extractTextFromMarkdown,
  joinSignatureLines,
  SIGNATURE_DELIMITER,
} from 'dashboard/helper/editorHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  signature: {
    type: String,
    default: '',
  },
});

const currentUser = useMapGetter('getCurrentUser');

const agentName = computed(
  () => currentUser.value?.available_name || currentUser.value?.name || ''
);
const agentAvatar = computed(() => currentUser.value?.avatar_url || '');

// chat gets the signature on a single line, the same one appendSignature builds
const chatSignature = computed(() =>
  extractTextFromMarkdown(joinSignatureLines(props.signature))
);
// email keeps the line breaks the agent typed
const emailSignature = computed(() => extractTextFromMarkdown(props.signature));

const hasSignature = computed(() => Boolean(chatSignature.value));
</script>

<template>
  <div
    class="grid gap-px overflow-hidden border rounded-xl border-n-weak bg-n-weak sm:grid-cols-2"
  >
    <div class="flex flex-col gap-3 p-4 bg-n-solid-1">
      <div class="flex flex-col gap-0.5">
        <span
          class="text-xs font-medium tracking-wide uppercase text-n-slate-11"
        >
          {{
            $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW.CHAT')
          }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{
            $t(
              'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW.CHAT_CHANNELS'
            )
          }}
        </span>
      </div>
      <div class="flex items-start gap-2">
        <Avatar
          :name="agentName"
          :src="agentAvatar"
          :size="28"
          rounded-full
          hide-offline-status
        />
        <div
          class="max-w-full px-3 py-2 rounded-lg rounded-tl-none bg-n-alpha-2 dark:bg-n-solid-3"
        >
          <p
            v-if="hasSignature"
            class="text-sm font-semibold break-words text-n-slate-12"
          >
            {{ chatSignature }}
          </p>
          <p v-else class="text-sm italic text-n-slate-10">
            {{
              $t(
                'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW.EMPTY'
              )
            }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{
              $t(
                'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW.SAMPLE_MESSAGE'
              )
            }}
          </p>
          <p class="mt-1 text-right text-xxs text-n-slate-10">
            {{
              $t(
                'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW.SAMPLE_TIME'
              )
            }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-3 p-4 bg-n-solid-1">
      <div class="flex flex-col gap-0.5">
        <span
          class="text-xs font-medium tracking-wide uppercase text-n-slate-11"
        >
          {{
            $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW.EMAIL')
          }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{
            $t(
              'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW.EMAIL_NOTE'
            )
          }}
        </span>
      </div>
      <div class="px-3 py-2 border rounded-lg border-n-weak">
        <p class="text-sm text-n-slate-11">
          {{
            $t(
              'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW.SAMPLE_MESSAGE'
            )
          }}
        </p>
        <p class="mt-2 text-sm text-n-slate-10">{{ SIGNATURE_DELIMITER }}</p>
        <p
          v-if="hasSignature"
          class="mt-2 text-sm break-words whitespace-pre-line text-n-slate-12"
        >
          {{ emailSignature }}
        </p>
        <p v-else class="mt-2 text-sm italic text-n-slate-10">
          {{
            $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.PREVIEW.EMPTY')
          }}
        </p>
      </div>
    </div>
  </div>
</template>
